import { Component, NgZone } from '@angular/core';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { CommonService } from '../../../shared/services/common.service';
import { CustomerSharedService } from '../customer-shared.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CustomerService } from '../customer.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-customer-management',
  templateUrl: './customer-management.component.html',
  styleUrl: './customer-management.component.scss'
})
export class CustomerManagementComponent {
  readonly noActionComplianceValue = 'no-action';
  complianceLevelOptions = [
    { value: 'high', label: 'High' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'no-action', label: 'No Action Required' },
  ];
  quickFilter: 'all' | 'active' | 'inactive' = 'all';
  apiCallLoaders = { isCustomerLoading: false };
  paginationConfig: NzTableQueryParams = { pageIndex: 1, pageSize: 10, sort: [], filter: [] };
  counts = { total_count: 0, active_count: 0, inactive_count: 0 };
  customerList$: any[] = [];
  filteredCustomerList$: any[] = [];
  searchText!: string;
  searchEnabled: boolean = false;

  isVisible: boolean = false;
  isEditMode: boolean = false;
  isConfirmLoading: boolean = false;
  editData: any = [];
  form!: FormGroup;
  applications: any[] = [];

  admin_name = localStorage.getItem('admin_name');

  constructor(
    public ngZone: NgZone,
    public customerService: CustomerService,
    public customerSharedService: CustomerSharedService,
    protected commonSvc: CommonService,
    public toaster: NzMessageService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.counts = { total_count: 0, active_count: 0, inactive_count: 0 };
    this.filteredCustomerList$ = [];
    this.getAllCustomers();
    this.initializeForm();
    this.getApplications();
  }

  initializeForm() {
    this.form = this.fb.group({
      company_name: [null, Validators.required],
      contact_person: [null, Validators.required],
      contact_email: [null, [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')]],
      contact_number: [null, [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      compliance_level: [[]],
      application_id: [[], Validators.required],
    });

    this.form.get('compliance_level')?.valueChanges.subscribe((selectedValues: string[] | null) => {
      this.normalizeComplianceLevelSelection(selectedValues);
    });
  }

  isComplianceOptionDisabled(optionValue: string): boolean {
    const selectedValues: string[] = this.form?.get('compliance_level')?.value || [];
    const hasNoAction = selectedValues.includes(this.noActionComplianceValue);
    const hasOtherValues = selectedValues.some(value => value !== this.noActionComplianceValue);

    if (optionValue === this.noActionComplianceValue) {
      return hasOtherValues;
    }
    return hasNoAction;
  }

  private normalizeComplianceLevelSelection(selectedValues: string[] | null): void {
    const values = Array.isArray(selectedValues) ? selectedValues : [];
    const hasNoAction = values.includes(this.noActionComplianceValue);
    const hasOtherValues = values.some(value => value !== this.noActionComplianceValue);

    if (hasNoAction && hasOtherValues) {
      this.form.get('compliance_level')?.setValue([this.noActionComplianceValue], { emitEvent: false });
    }
  }

  getApplications() {
    this.customerService.getApplications({}).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        if (response?.data) {
          this.applications = response.data;
        }
      },
      error: (error: any) => {
        console.error('Error fetching applications:', error);
      }
    });
  }

  refreshHandler(): void {
    this.paginationConfig = { pageIndex: 1, pageSize: 10, sort: [], filter: [] };
    this.counts = { total_count: 0, active_count: 0, inactive_count: 0 };
    this.quickFilter = 'all';
    this.searchText = '';
    this.searchEnabled = false;
    this.customerList$ = [];
    this.filteredCustomerList$ = [];
    this.getAllCustomers();
  }

  getAllCustomers() {
    const requestData = {};
    this.ngZone.runOutsideAngular(() => {
      this.apiCallLoaders.isCustomerLoading = true;
      this.customerService.getAllCustomers(requestData).pipe(untilDestroyed(this)).subscribe(
        (response: any) => {
          if (response?.status === 200 && response?.data) {
            this.ngZone.run(() => {
              this.customerList$ = response.data;
              this.filteredCustomerList$ = [...this.customerList$]
              this.customerList$.forEach(customer => {
                this.counts.total_count++;
                if (customer?.is_active === true) this.counts.active_count++;
                if (customer?.is_active === false) this.counts.inactive_count++;
              });
              this.apiCallLoaders.isCustomerLoading = false;
            });
          } else {
            this.ngZone.run(() => {
              this.apiCallLoaders.isCustomerLoading = false;
              this.toaster.info('No data available or invalid response status.', { nzDuration: 5000 });
              console.error('No data available or invalid response status.');
            });
          }
        }
      );
    });
  }

  filterData(): void {
    if (this.quickFilter === 'all') {
      this.filteredCustomerList$ = this.customerList$;
    } else if (this.quickFilter === 'active') {
      this.filteredCustomerList$ = this.customerList$.filter(customer => customer.is_active);
    } else if (this.quickFilter === 'inactive') {
      this.filteredCustomerList$ = this.customerList$.filter(customer => !customer.is_active);
    }
  }

  getSearchDetails(val: string): void {
    let searchText = val.trim().toLowerCase();
    this.filteredCustomerList$ = this.customerList$.filter(customer => {
      return (customer.customer_name && customer.customer_name.toLowerCase().includes(searchText)) ||
        (customer.contact_email && customer.contact_email.toLowerCase().includes(searchText)) ||
        (customer.contact_person && customer.contact_person.toLowerCase().includes(searchText)) ||
        (customer.contact_number && customer.contact_number.toLowerCase().includes(searchText)) ||
        (customer.company_name && customer.company_name.toLowerCase().includes(searchText))
    });
  }

  onSubmit(): void {
    if (this.isConfirmLoading || !this.commonSvc.validate(this.form)) return;

    this.isConfirmLoading = true;
    const apiCall = this.isEditMode ? this.handleUpdate() : this.handleAdd();

    if (!apiCall) {
      this.isConfirmLoading = false;
      return;
    }

    apiCall.pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => this.handleSuccess(response),
      error: (error: any) => this.handleError(error)
    });
  }

  private getFormData(): any {
    return {
      company_name: this.form.value.company_name,
      contact_person: this.form.value.contact_person,
      contact_email: this.form.value.contact_email,
      contact_number: this.form.value.contact_number,
      compliance_level: this.form.value.compliance_level,
      application_id: this.form.value.application_id,
    };
  }

  private getOriginalData(): any {
    return {
      company_name: this.editData?.company_name,
      contact_person: this.editData?.contact_person,
      contact_email: this.editData?.contact_email,
      contact_number: this.editData?.contact_number,
      compliance_level: this.commonSvc.parseJsonArray(this.editData?.compliance_level),
      application_id: Array.isArray(this.editData?.application_id)
        ? this.editData.application_id
        : (this.editData?.application_id ? [this.editData.application_id] : []),
    };
  }

  private handleAdd() {
    const payload = {
      ...this.getFormData(),
      last_update_user: this.admin_name,
    };

    const checklistConfig = this.customerSharedService.getChecklistConfig();
    if (checklistConfig) {
      payload['checklist'] = checklistConfig;
    }

    return this.customerService.addCustomer(payload);
  }

  private handleUpdate() {
    const changedFields = this.commonSvc.getChangedFields(this.getOriginalData(), this.getFormData());

    if (Object.keys(changedFields).length === 0) {
      this.commonSvc.message.info('No changes detected');
      return null;
    }

    changedFields['last_update_user'] = this.admin_name;
    return this.customerService.updateCustomer(this.editData.customer_id, changedFields);
  }

  private handleSuccess(response: any): void {
    if (response?.status === 200) {
      this.commonSvc.message.success(
        this.isEditMode ? 'Customer updated successfully' : 'Customer added successfully'
      );
      this.isVisible = false;
      this.getAllCustomers();
    } else {
      this.commonSvc.message.error(
        response?.message || (this.isEditMode ? 'Failed to update customer' : 'Failed to add customer')
      );
    }
    this.isConfirmLoading = false;
  }

  private handleError(error: any): void {
    this.commonSvc.message.error(
      this.isEditMode ? 'An error occurred while updating the customer' : 'An error occurred while adding the customer'
    );
    console.error('Error:', error);
    this.isConfirmLoading = false;
  }

  addEditCustomer(type: string, data?: any): void {
    this.isEditMode = type === 'edit';
    this.editData = data;

    if (this.isEditMode) {
      this.form.patchValue({
        company_name: data?.company_name,
        contact_person: data?.contact_person,
        contact_email: data?.contact_email,
        contact_number: data?.contact_number,
        compliance_level: this.commonSvc.parseJsonArray(data?.compliance_level),
        application_id: Array.isArray(data?.application_id) ? data.application_id : (data?.application_id ? [data.application_id] : [])
      });
    } else {
      this.form.reset();
      this.form.patchValue({ compliance_level: [], application_id: [] });
    }
    this.isVisible = true;
  }

}
